import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import {  TabBar, InfoCard  } from "@/components/toggles";
import { ArrowLeft, Plus, Trash2, Edit3, Save, X, Film, Image as ImageIcon, BarChart3 } from "lucide-react";
import RichTextEditor from "@/components/common/RichTextEditor";
import { StoryInsightsSheet, StoryHighlightsManager } from "@/components/feed/StoryStudioExtras";
import AIAssistBar from "@/components/ai/AIAssistBar";
import MonetizationModeSelector from "@/components/common/MonetizationModeSelector";
import UploadingBadge from "@/components/common/UploadingBadge";
import ImageCropDialog from "@/components/home/ImageCropDialog";

/**
 * StudioSocialPage (iter80.10) — Beiträge editieren/löschen + Stories konsistent.
 * Feed-Post-Erstellung + Story-Erstellung nutzen jetzt den globalen FeedComposer
 * (via 'open-feed-composer'-Event) statt eigener veralteter Inline-Wizards.
 */
export default function StudioSocialPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/studio/social/posts");
      setPosts(data.posts || []);
    } catch { setPosts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    loadPosts();
    // Nach Publish vom globalen Composer → reload
    const onPublished = () => loadPosts();
    window.addEventListener("feed-post-published", onPublished);
    return () => window.removeEventListener("feed-post-published", onPublished);
  }, [user, navigate, loadPosts]);

  const openComposer = (mode) => {
    window.dispatchEvent(new CustomEvent("open-feed-composer", { detail: { mode } }));
  };

  return (
    <div data-testid="studio-social-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="social-back" onClick={() => navigate("/studio")} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A] flex-1">Social</h1>
      </div>

      <TabBar
        tabs={[
          { key: "posts", label: "Feed Posts", testId: "social-tab-posts" },
          { key: "stories", label: "Stories", testId: "social-tab-stories" },
        ]}
        activeKey={tab}
        onChange={setTab}
      />

      {tab === "posts"
        ? <PostsTab posts={posts} loading={loading} onRefresh={loadPosts} onCreate={() => openComposer("post")} />
        : <StoriesTab userId={user?._id || user?.id} onOpenComposer={() => openComposer("story")} />}
    </div>
  );
}

function PostsTab({ posts, loading, onRefresh, onCreate }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(null); // {post_id, title, description, media_url}
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    if (!editing?.title?.trim()) return;
    setSaving(true); setError("");
    try {
      // iter232 · Post-Edit sendet jetzt monetization_mode + price_eur mit.
      await api.patch(`/studio/social/posts/${editing.post_id}`, {
        title: editing.title.trim(),
        description: editing.description || "",
        media_url: editing.media_url || undefined,
        visible_tiers: editing.visible_tiers || [],
        monetization_mode: editing.monetization_mode || undefined,
        price_eur: editing.price_eur ?? undefined,
      });
      setEditing(null);
      onRefresh();
    } catch (e) { setError(formatError(e) || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  const [cropFile, setCropFile] = useState(null);

  const uploadMedia = (e) => {
    // iter169.12 · Crop-Tool vor Upload aufrufen (16:9 identisch zu FeedComposer).
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editing) return;
    setError("");
    setCropFile(file);
  };

  const uploadCropped = async (blob) => {
    setUploading(true); setError("");
    const fd = new FormData();
    fd.append("file", blob, blob.name || "post.jpg");
    try {
      // iter232 · Upload-Fix: Content-Type NICHT hart setzen (axios braucht die Boundary).
      const { data } = await api.post("/upload?area_scope=SOCIAL", fd);
      const url = `/api/files/${data.path}`;
      setEditing({ ...editing, media_url: url });
      // iter173b · Warnung bei Body-Content im Studio-Post-Edit (User-Vorgabe 2026-02-28)
      if (data?.explicit_detected) {
        setError(t("feed.body_content_detected"));
      }
    } catch (err) { setError(formatError(err) || "Upload fehlgeschlagen"); }
    finally { setUploading(false); }
  };

  const remove = async (post_id) => {
    if (!window.confirm(t("studio_social.confirm_delete_post"))) return;
    try {
      await api.delete(`/studio/social/posts/${post_id}`);
      onRefresh();
    } catch { window.alert(t("studio_social.delete_failed")); }
  };

  return (
    <div data-testid="posts-tab">
      <button data-testid="create-post-btn" onClick={onCreate}
        className="w-full flex items-center gap-2 px-4 py-4 border-b border-[#E5E5E5] text-left hover:bg-[#FAFAFA] transition-colors">
        <Plus size={18} className="text-[#999]" />
        <span className="text-sm text-[#999]">{t("studio_social.new_post_ph")}</span>
      </button>

      {loading ? (
        <p className="px-4 py-12 text-center text-sm text-[#999]">{t("studio_social.loading")}</p>
      ) : posts.length === 0 ? (
        <p className="px-4 py-12 text-center text-sm text-[#999]">{t("studio_social.no_posts")}</p>
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {posts.map((p) => (
            <div key={p.post_id} data-testid={`studio-post-${p.post_id}`} className="px-4 py-3">
              {editing?.post_id === p.post_id ? (
                <div className="space-y-2">
                  {error && <p className="text-xs text-red-500">{error}</p>}
                  <input
                    data-testid={`studio-post-edit-title-${p.post_id}`}
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    className="w-full rounded-full h-9 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                    placeholder={t("studio_social.headline")}
                    maxLength={120}
                  />
                  <RichTextEditor
                    data-testid={`studio-post-edit-desc-${p.post_id}`}
                    value={editing.description}
                    onChange={(v) => setEditing({ ...editing, description: v })}
                    placeholder="Beschreibung (optional)"
                    minHeight={100}
                  />
                  <AIAssistBar
                    area="SOCIAL"
                    value={editing.description}
                    onApply={(v) => setEditing({ ...editing, description: v })}
                    fieldType="description"
                    testIdSuffix={`studio-post-${p.post_id}`}
                  />
                  {/* iter122-fix: Bild-Änderung im Edit-Modus */}
                  {editing.media_url && (
                    <div className="relative w-full max-w-[240px]">
                      <img src={editing.media_url} alt="" className="w-full aspect-video object-cover rounded border border-[#E5E5E5]" />
                      <button
                        data-testid={`studio-post-remove-media-${p.post_id}`}
                        onClick={() => setEditing({ ...editing, media_url: "" })}
                        className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 px-3 py-1.5 border border-[#E5E5E5] text-xs text-[#666] cursor-pointer hover:bg-[#F5F5F5] rounded">
                      <ImageIcon size={14} /> {editing.media_url ? "Bild ersetzen" : "Bild hinzufügen"}
                      <input data-testid={`studio-post-edit-file-${p.post_id}`} type="file" accept="image/*" className="hidden" onChange={uploadMedia} disabled={uploading} />
                    </label>
                    <UploadingBadge active={uploading} testid={`studio-post-edit-uploading-${p.post_id}`} />
                  </div>
                  {/* iter232 · Post-Edit: Voller Modus-Selector (statt nur Tier).
                      Backend akzeptiert monetization_mode via /studio/social/posts PATCH. */}
                  <MonetizationModeSelector
                    mode={editing.monetization_mode || (editing.visible_tiers?.length ? "SUBSCRIBERS_ONLY" : "FREE")}
                    onModeChange={(m) => setEditing({ ...editing, monetization_mode: m })}
                    tiers={editing.visible_tiers || []}
                    onTiersChange={(v) => setEditing({ ...editing, visible_tiers: v })}
                    price={editing.price_eur ?? ""}
                    onPriceChange={(v) => setEditing({ ...editing, price_eur: v === "" ? null : v })}
                    testIdPrefix={`studio-post-mode-${p.post_id}`}
                  />
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(null)}
                      className="rounded-full h-8 px-3 border border-[#E5E5E5] text-[10px] text-[#666] flex items-center gap-1">
                      <X size={10} /> Abbrechen
                    </button>
                    <button data-testid={`studio-post-edit-save-${p.post_id}`}
                      onClick={save} disabled={saving || uploading || !editing.title?.trim()}
                      className="h-8 px-3 bg-[#0A0A0A] text-white text-[10px] flex items-center gap-1 disabled:opacity-30 rounded-full">
                      <Save size={10} /> {saving ? "..." : "Speichern"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm font-semibold text-[#0A0A0A]">{p.title || p.text || "(ohne Titel)"}</p>
                  {p.description && (
                    <div className="text-xs text-[#666] mt-1 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: p.description }} />
                  )}
                  {p.media_url && (
                    <img src={p.media_url} alt="" className="mt-2 w-full max-w-[240px] aspect-video object-cover rounded border border-[#E5E5E5]" />
                  )}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="text-[10px] text-[#999] flex-1">{p.status} · {new Date(p.created_at).toLocaleDateString("de-DE")}</span>
                    {(p.visible_tiers || []).length === 0 ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700">{t("studio_social.public")}</span>
                    ) : (
                      (p.visible_tiers || []).map((tv) => (
                        <span key={tv} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 uppercase">{tv}</span>
                      ))
                    )}
                    <button
                      data-testid={`studio-post-edit-${p.post_id}`}
                      onClick={() => setEditing({ post_id: p.post_id, title: p.title || p.text || "", description: p.description || "", media_url: p.media_url || "", visible_tiers: p.visible_tiers || [], monetization_mode: p.monetization_mode || (p.visible_tiers?.length ? "SUBSCRIBERS_ONLY" : "FREE"), price_eur: p.price_eur ?? "" })}
                      className="p-1 text-[#666] hover:text-[#0A0A0A]" title={t("studio_social.edit")}>
                      <Edit3 size={14} />
                    </button>
                    <button
                      data-testid={`studio-post-delete-${p.post_id}`}
                      onClick={() => remove(p.post_id)}
                      className="p-1 text-red-400 hover:text-red-600" title={t("studio_social.delete")}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {cropFile && (
        <ImageCropDialog
          file={cropFile}
          aspect={16 / 9}
          title="Post-Bild zuschneiden (16:9)"
          onCancel={() => setCropFile(null)}
          onCropped={async (blob) => { setCropFile(null); await uploadCropped(blob); }}
        />
      )}
    </div>
  );
}

function StoriesTab({ userId, onOpenComposer }) {
  const { t } = useTranslation();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [insights, setInsights] = useState(null); // story_id for insights sheet

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/stories/by-profile/${userId}`);
      setStory(data.story);
    } catch (e) {
      if (e.response?.status === 404) setStory(null);
      else setError(t("studio_social.load_story_error"));
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => {
    load();
    const onPub = () => load();
    window.addEventListener("feed-post-published", onPub);
    return () => window.removeEventListener("feed-post-published", onPub);
  }, [load]);

  const remove = async () => {
    if (!story || !window.confirm(t("studio_social.confirm_delete_story"))) return;
    try {
      await api.delete(`/studio/social/stories/${story.story_id}`);
      setStory(null);
    } catch { window.alert(t("studio_social.delete_failed")); }
  };

  return (
    <div data-testid="stories-tab" className="px-4 py-4 space-y-4">
      {error && <InfoCard variant="danger" size="sm">{error}</InfoCard>}

      {loading ? (
        <p className="text-center text-sm text-[#999] py-8">{t("studio_social.loading")}</p>
      ) : story ? (
        <div data-testid="studio-active-story" className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-[#0A0A0A]">{t("studio_social.active_story", { defaultValue: "Aktive Story" })}</p>
            <p className="text-[10px] text-[#999]">
              {t("studio_social.story_expires", { defaultValue: "Läuft ab:" })} {new Date(story.expires_at).toLocaleString(undefined, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {(story.slides || []).map((s, i) => (
              <div key={i} data-testid={`studio-story-slide-${i}`}
                className="relative aspect-square bg-[#0A0A0A] overflow-hidden">
                {s.media_type === "VIDEO" ? (
                  <video src={s.media_url} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                ) : (
                  <img src={s.media_url} alt="" className="w-full h-full object-cover" />
                )}
                <span className="absolute top-1 left-1 bg-black/60 text-white text-[9px] px-1 rounded">{i + 1}</span>
                {s.media_type === "VIDEO" && s.duration_sec != null && (
                  <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[9px] px-1 rounded">{Math.round(s.duration_sec)}s</span>
                )}
              </div>
            ))}
          </div>
          {story.caption && <p className="text-xs text-[#666]">&bdquo;{story.caption}&ldquo;</p>}
          <div className="flex gap-2">
            <button data-testid="studio-story-edit"
              onClick={() => window.dispatchEvent(new CustomEvent("open-feed-composer", { detail: { mode: "story", area: "SOCIAL", editStory: story } }))}
              className="flex-1 h-9 bg-[#0A0A0A] text-white text-xs font-medium flex items-center justify-center gap-1">
              <Edit3 size={12} /> {t("studio_social.edit_story", { defaultValue: "Story bearbeiten" })}
            </button>
            <button data-testid="studio-story-delete" onClick={remove}
              className="h-9 px-3 border border-red-300 text-red-500 text-xs flex items-center gap-1">
              <Trash2 size={12} /> {t("studio_social.delete_label", { defaultValue: "Löschen" })}
            </button>
          </div>
          <button data-testid="studio-story-replace" onClick={onOpenComposer}
            className="w-full h-9 border border-[#E5E5E5] text-[#666] text-xs font-medium flex items-center justify-center gap-1 hover:bg-[#FAFAFA]">
            <Plus size={12} /> {t("studio_social.new_story_replaces", { defaultValue: "Neue Story (ersetzt aktuelle)" })}
          </button>
          <button data-testid="studio-story-insights" onClick={() => setInsights(story.story_id)}
            className="w-full h-9 border border-[#E5E5E5] text-[#0A0A0A] text-xs font-medium flex items-center justify-center gap-1 hover:bg-[#FAFAFA]">
            <BarChart3 size={12} /> {t("stories_extra.view_insights", { defaultValue: "Zuschauer & Insights" })}
          </button>
          <p className="text-[10px] text-[#999]">
            {t("studio_social.story_edit_help", { defaultValue: "„Bearbeiten“ fügt Bilder/Videos hinzu oder entfernt sie — jedes Element bleibt ab seinem eigenen Upload 24 Std. sichtbar. „Neue Story“ ersetzt die komplette aktuelle Story." })}
          </p>
        </div>
      ) : (
        <button data-testid="create-story-btn" onClick={onOpenComposer}
          className="w-full h-32 border-2 border-dashed border-[#E5E5E5] flex flex-col items-center justify-center gap-2 hover:border-[#999] transition-colors">
          <Film size={24} className="text-[#CCC]" />
          <span className="text-sm text-[#999]">{t("studio_social.new_story")}</span>
        </button>
      )}
      <StoryHighlightsManager />
      {insights && <StoryInsightsSheet storyId={insights} onClose={() => setInsights(null)} />}
    </div>
  );
}
